import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { browserName, getUA, isMobile } from "react-device-detect";
import * as amplitude from "@amplitude/analytics-browser";
import {
  getInAppBrowserKind,
  getMobileBrowserKind,
  InAppBrowserKind,
  isAndroidDevice,
  isIosDevice,
  isStandaloneDisplayMode,
} from "@/utils/browser-environment";

/**
 * PWA 설치 경로를 안내하는 배너의 노출 판정.
 *
 * 플랫폼마다 설치가 되는 브라우저가 하나뿐이라(iOS는 Safari, 안드로이드는 Chrome으로
 * 수렴), 그 브라우저면 설치 단계를 안내하고 아니면 그 브라우저로 유도한다.
 *
 * beforeinstallprompt는 구독하지 않는다. 이 앱에는 서비스워커가 없어 프로덕션에서
 * 발화하지 않고, preventDefault를 부르면 브라우저 기본 배너만 죽이게 된다.
 */

export type PwaBannerVariant =
  | "ios-safari-guide"
  | "ios-open-safari"
  | "android-chrome-guide"
  | "android-open-chrome";

/** 배너를 띄우지 않기로 한 이유. 계측과 개발용 진단에만 쓴다. */
type SuppressReason =
  | "not-mobile"
  | "unsupported-os"
  | "standalone"
  | "in-app-browser"
  | "dismissed";

// 문구나 전략을 바꿀 때 전원에게 다시 보여주려면 키 버전을 올린다.
const STORAGE_KEY = "pathrade.pwaBanner.v1";

const DAY_MS = 24 * 60 * 60 * 1000;
// 닫을수록 길게 쉰다. 세 번 닫으면 다시 묻지 않는다.
const COOLDOWN_DAYS = [3, 14];
const PERMANENT_DISMISS_COUNT = COOLDOWN_DAYS.length + 1;

// 랜딩 히어로 이미지(_app.tsx가 preload한다)를 첫인상에서 가리지 않는다.
const SHOW_DELAY_MS = 4000;

// 폼 하단이 가려지고 iOS 키보드와 겹친다 / 몰입형 전체화면 플로우.
const EXCLUDED_PATH_PATTERNS = [
  /(^|\/)auth(\/|$)/,
  /naver-solution\/onboarding/,
];

interface DismissState {
  dismissCount: number;
  dismissedAt: number;
  neverShow: boolean;
}

const INITIAL_DISMISS_STATE: DismissState = {
  dismissCount: 0,
  dismissedAt: 0,
  neverShow: false,
};

// 시크릿 모드 등에서는 localStorage 접근 자체가 throw한다.
function readDismissState(): DismissState {
  if (typeof window === "undefined") {
    return INITIAL_DISMISS_STATE;
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return INITIAL_DISMISS_STATE;
    }
    const parsed = JSON.parse(raw);
    return {
      dismissCount:
        typeof parsed?.dismissCount === "number" ? parsed.dismissCount : 0,
      dismissedAt:
        typeof parsed?.dismissedAt === "number" ? parsed.dismissedAt : 0,
      neverShow: parsed?.neverShow === true,
    };
  } catch (e) {
    return INITIAL_DISMISS_STATE;
  }
}

function writeDismissState(state: DismissState) {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (e) {
    // 용량 초과 / 사생활 모드는 무시한다. 이번 세션만 닫힌 상태로 남는다.
  }
}

function isDismissed(state: DismissState) {
  if (state.neverShow || state.dismissCount >= PERMANENT_DISMISS_COUNT) {
    return true;
  }
  if (state.dismissCount <= 0) {
    return false;
  }
  const cooldownDays = COOLDOWN_DAYS[state.dismissCount - 1];
  return Date.now() - state.dismissedAt < cooldownDays * DAY_MS;
}

interface EnvironmentDecision {
  variant: PwaBannerVariant | null;
  reason: SuppressReason | null;
  inAppKind: InAppBrowserKind | null;
}

/**
 * 환경만 보고 판정한다. 경로·지연·입력 상태는 호출부에서 따로 얹는다.
 * 여기서 걸리는 조건들은 세션 중에 바뀌지 않아 마운트 시 한 번만 계산한다.
 */
function resolveEnvironment(): EnvironmentDecision {
  // 레이아웃 분기(private-layout의 BrowserView/MobileView)와 같은 판별식을 쓴다.
  // isMobile은 태블릿과 iPadOS 13+ desktop-mode Safari를 포함한다.
  if (!isMobile) {
    return { variant: null, reason: "not-mobile", inAppKind: null };
  }

  if (isStandaloneDisplayMode()) {
    return { variant: null, reason: "standalone", inAppKind: null };
  }

  // 어느 앱의 인앱인지는 계측용으로만 쓴다. 노출 여부는 아래 화이트리스트가 정한다.
  const inAppKind = getInAppBrowserKind();

  if (isDismissed(readDismissState())) {
    return { variant: null, reason: "dismissed", inAppKind };
  }

  switch (getMobileBrowserKind()) {
    // iOS에서 standalone 실행이 되는 건 Safari뿐이다. Chrome iOS의 '홈 화면에 추가'는
    // Chrome으로 열리는 단축키라, 같은 안내를 하면 설치한 줄 착각하게 된다.
    case "ios-safari":
      return { variant: "ios-safari-guide", reason: null, inAppKind };
    case "ios-other":
      return { variant: "ios-open-safari", reason: null, inAppKind };
    // 안드로이드도 Chrome 하나로 수렴시킨다 — 삼성인터넷은 WebAPK를 만들 수 있지만
    // 설치한 브라우저가 그 앱의 엔진·저장소 주인이 되므로 경로를 하나로 모은다.
    case "android-chrome":
      return { variant: "android-chrome-guide", reason: null, inAppKind };
    case "android-other":
      return { variant: "android-open-chrome", reason: null, inAppKind };
    // WebView에는 홈 화면 추가 메뉴가 없어서 안내할 대상이 없다.
    case "webview":
      return { variant: null, reason: "in-app-browser", inAppKind };
    default:
      return { variant: null, reason: "unsupported-os", inAppKind };
  }
}

function isEditableElement(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  const tagName = target.tagName;
  return (
    tagName === "INPUT" ||
    tagName === "TEXTAREA" ||
    tagName === "SELECT" ||
    target.isContentEditable
  );
}

export default function usePwaInstallBanner() {
  // 초기값은 반드시 null이어야 한다. mocking을 끈 환경에서는 use-mock의 shouldRender가
  // true로 시작해 이 트리가 하이드레이션에 참여하므로, 여기서 localStorage를 동기로 읽으면
  // 서버/클라이언트 첫 렌더가 어긋난다. 기본이 '숨김'이라 effect 판정으로도 깜빡임은 없다.
  const [decision, setDecision] = useState<EnvironmentDecision | null>(null);
  const [isDelayPassed, setIsDelayPassed] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const location = useLocation();

  useEffect(() => {
    const resolved = resolveEnvironment();
    setDecision(resolved);

    // 판별이 UA·navigator에 의존해서 기기마다 결과가 다르다. 원격 디버깅으로
    // "왜 안 뜨는지"를 한 번에 보려면 판정 입력값이 같이 찍혀야 한다.
    if (process.env.NODE_ENV !== "production") {
      const nav = window.navigator as Navigator & { standalone?: boolean };
      // console.log("[PwaBanner]", {
      //   variant: resolved.variant,
      //   reason: resolved.reason,
      //   inAppKind: resolved.inAppKind,
      //   browserName,
      //   browserKind: getMobileBrowserKind(),
      //   ua: getUA,
      //   isMobile,
      //   isIos: isIosDevice(),
      //   isAndroid: isAndroidDevice(),
      //   isStandalone: isStandaloneDisplayMode(),
      //   platform: nav.platform,
      //   maxTouchPoints: nav.maxTouchPoints,
      // });
    }

    if (resolved.reason) {
      amplitude.track("PWA Banner Suppressed", {
        reason: resolved.reason,
        inAppKind: resolved.inAppKind,
        browserName,
      });
    }
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(
      () => setIsDelayPassed(true),
      SHOW_DELAY_MS
    );
    return () => window.clearTimeout(timer);
  }, []);

  // iOS 사파리는 키보드가 올라올 때 fixed 요소를 키보드 위로 띄운다.
  // 입력 중에 배너가 입력창을 덮지 않게 잠시 접는다.
  useEffect(() => {
    const handleFocusIn = (event: FocusEvent) => {
      if (isEditableElement(event.target)) {
        setIsEditing(true);
      }
    };
    const handleFocusOut = (event: FocusEvent) => {
      // 입력창끼리 포커스를 옮기는 중에는 계속 접어둔다.
      // relatedTarget 을 보지 않으면 focusout→focusin 사이에 한 프레임 깜빡인다.
      if (!isEditableElement(event.relatedTarget)) {
        setIsEditing(false);
      }
    };

    document.addEventListener("focusin", handleFocusIn);
    document.addEventListener("focusout", handleFocusOut);
    return () => {
      document.removeEventListener("focusin", handleFocusIn);
      document.removeEventListener("focusout", handleFocusOut);
    };
  }, []);

  const isExcludedPath = useMemo(
    () =>
      EXCLUDED_PATH_PATTERNS.some((pattern) => pattern.test(location.pathname)),
    [location.pathname]
  );

  const variant =
    decision?.variant && isDelayPassed && !isEditing && !isExcludedPath
      ? decision.variant
      : null;

  // 노출 계측은 세션당 한 번만. 입력·라우팅으로 접혔다 펴질 때마다 쌓이면 안 된다.
  const hasTrackedShown = useRef(false);
  useEffect(() => {
    if (variant && !hasTrackedShown.current) {
      hasTrackedShown.current = true;
      amplitude.track("PWA Banner Shown", { variant, browserName });
    }
  }, [variant]);

  const close = useCallback(
    (isForever: boolean) => {
      const previous = readDismissState();
      const next: DismissState = {
        dismissCount: previous.dismissCount + 1,
        dismissedAt: Date.now(),
        neverShow: isForever,
      };
      writeDismissState(next);

      amplitude.track("PWA Banner Dismissed", {
        variant: decision?.variant,
        isForever,
        dismissCount: next.dismissCount,
        browserName,
      });

      setDecision({ variant: null, reason: "dismissed", inAppKind: null });
    },
    [decision]
  );

  const dismiss = useCallback(() => close(false), [close]);
  const dismissForever = useCallback(() => close(true), [close]);

  return { variant, dismiss, dismissForever };
}
